import React, { useState, useCallback, useMemo } from "react";
import Select, { components } from "react-select";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";

import { Input } from "./Previews/Components";
import { validateVatNumber } from "./utils";

import * as sc from "./Previews/Components/Input/styles";
import { COUNTRIES_WITHOUT_TAXES } from "./campaign-creation/invoice-address-modal/utils";

function mapAddressToString(address = {}) {
  let addressString = [address.street, [address.zip, address.city].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join("\n");

  return addressString;
}

const InvoiceOptionWrapper = styled.div`
  cursor: pointer;
  transition: all 0.15s;

  ${(props) =>
    props.button
      ? css`
          padding: 6px;
          background-color: white;
          border: 2px solid #2984ff;
          border-radius: 6px;
          width: 95%;
          margin: 6px auto 2px;
          display: flex;
          align-items: center !important;

          &:hover {
            color: white;
            background-color: #2984ff;
          }
        `
      : css`
          padding: 8px 12px;
          width: 100%;
          background-color: ${props.isSelected ? "#2584ff" : "white"};

          &:hover {
            background-color: ${props.isSelected ? "#2584ff" : "#ddebff"};
          }
        `}
`;

const MainWrapper = styled.div``;

const SingleFieldWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 14px;

  ${(props) =>
    props.group
      ? css`
          flex-direction: column;
          align-items: flex-start;
        `
      : css`
          flex-direction: row;
          justify-content: space-between;
        `}

  > label {
    color: #191d23;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    margin-bottom: 4px;
    margin-right: 10px;
  }

  ${sc.Input} {
    width: 100%;
  }
`;

const Fieldset = styled.div`
  display: flex;

  ${(props) =>
    props.group
      ? css`
          flex-direction: row;

          ${SingleFieldWrapper}:first-child {
            width: 48%;
            flex: 1 1 auto;
            margin-right: 14px;
          }

          ${SingleFieldWrapper}:last-child {
            width: 48%;
            flex: 1 1 auto;
          }
        `
      : css`
          flex-direction: column;
        `}
`;

const InvoiceOption = ({ innerProps, data, isSelected }) => {
  return (
    <InvoiceOptionWrapper {...innerProps} isSelected={isSelected} button={data.value === "new"}>
      <div>
        <strong>{data.label}</strong>
      </div>
      {data.address && (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {data.address}
        </div>
      )}
      {data.email && <div>{data.email}</div>}
    </InvoiceOptionWrapper>
  );
};

const InvoiceSelected = ({ children, ...props }) => {
  return (
    <components.SingleValue {...props}>
      <div>
        <strong>{props.data.label}</strong>
      </div>
      <div
        style={{
          marginBottom: "3px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}
      >
        {props.data.address}
      </div>
      <div>{props.data.email}</div>
    </components.SingleValue>
  );
};

const InvoiceAddressSelector = ({
  countryCode,
  currentlySelectedAddress,
  direction = "bottom",
  disabled = false,
  invoiceAddresses,
  isCheckout,
  onSelectAddress,
  selectedInvoiceAddressData = {},
  selectedInvoiceEmail = "",
  setSelectedInvoiceAddressData = () => {},
  setSelectedInvoiceEmail = () => {},
  setVatNumber,
  showEmail = true
}) => {
  const { t } = useTranslation();
  const [vatNumberValid, setVATNumberValid] = useState(true);

  const formattedInvoiceOptions = useMemo(() => {
    return [
      ...invoiceAddresses.map(({ id, name, address, email }) => ({
        value: id,
        label: name,
        address,
        email
      })),
      {
        value: "new",
        label: `+ ${t("Create billing address")}`
      }
    ];
  }, [invoiceAddresses, t]);

  if (direction == "top") {
    formattedInvoiceOptions.reverse();
  }

  const valueSelected = formattedInvoiceOptions?.find(
    ({ value, label }) =>
      value === currentlySelectedAddress?.id && label === currentlySelectedAddress?.name
  );

  const formattedAddressOptions = useMemo(
    () =>
      currentlySelectedAddress?.addresses?.map(({ id, ...rest }) => ({
        value: id,
        label: mapAddressToString(rest)
      })),
    [currentlySelectedAddress]
  );

  const currentlySelectedAddressOption = useMemo(
    () => formattedAddressOptions?.find(({ value }) => value === selectedInvoiceAddressData?.id),
    [formattedAddressOptions, selectedInvoiceAddressData]
  );

  const formattedEmailOptions = useMemo(
    () =>
      currentlySelectedAddress?.emails?.map(({ id, value }) => ({
        value: id,
        label: value
      })),
    [currentlySelectedAddress]
  );

  const currentlySelectedEmailOption = useMemo(
    () => formattedEmailOptions?.find(({ label }) => label === selectedInvoiceEmail),
    [formattedEmailOptions, selectedInvoiceEmail]
  );

  const onSelectAddressData = useCallback(
    ({ value }) => {
      const selectedAddressData = currentlySelectedAddress?.addresses.find(
        ({ id }) => id === value
      );
      setSelectedInvoiceAddressData(selectedAddressData);
    },
    [currentlySelectedAddress, setSelectedInvoiceAddressData]
  );

  const onSelectEmail = useCallback(
    ({ value }) => {
      const selectedEmail = currentlySelectedAddress?.emails?.find(({ id }) => id === value)?.value;
      setSelectedInvoiceEmail(selectedEmail);
    },
    [currentlySelectedAddress, setSelectedInvoiceEmail]
  );

  function handleValidateVatNumber(value) {
    const isValid = validateVatNumber(value);
    setVATNumberValid(isValid);

    if (isValid) {
      setVatNumber(value);
    }
  }

  const SingleValueComponent = isCheckout ? null : { SingleValue: InvoiceSelected };
  const styles = {
    container: isCheckout ? { display: "inline-block", width: "100%" } : { width: "250px" },
    control: { borderColor: "#a0abbb" },
    valueContainer: {
      "input[readonly]": {
        position: "absolute"
      }
    },
    singleValue: {
      color: "#444",
      fontFamily: "Inter",
      fontSize: 14,
      fontStyle: "normal",
      fontWeight: 400,
      maxWidth: "200px",
      overflow: "hidden",
      padding: "5px 0",
      position: "relative",
      textAlign: "left",
      textOverflow: "ellipsis",
      transform: "none",
      whiteSpace: "nowrap",
      width: "100%"
    },
    indicatorsContainer: {
      padding: 0,
      svg: { fill: "#444" }
    },
    menuList: { justifyContent: "flex-start !important", maxHeight: "250px" }
  };

  return (
    <MainWrapper>
      <Fieldset group={isCheckout}>
        <SingleFieldWrapper group={isCheckout}>
          <label>{t("Company")}</label>
          <Select
            isSearchable={false}
            placeholder={t("Billing address")}
            options={formattedInvoiceOptions}
            value={valueSelected}
            onChange={onSelectAddress}
            components={{
              Option: InvoiceOption,
              IndicatorSeparator: null,
              ...SingleValueComponent
            }}
            menuPlacement={direction}
            isDisabled={disabled}
            styles={{
              singleValue: (provided) => ({
                ...provided,
                ...styles.singleValue
              }),
              menuList: (provided) => ({
                ...provided,
                ...styles.menuList
              }),
              container: (provided) => ({
                ...provided,
                ...styles.container
              }),
              control: (provided) => ({
                ...provided,
                ...styles.control
              }),
              indicatorsContainer: (provided) => ({
                ...provided,
                ...styles.indicatorsContainer
              }),
              valueContainer: (provided) => ({
                ...provided,
                ...styles.valueContainer
              })
            }}
          />
        </SingleFieldWrapper>

        {showEmail && (
          <SingleFieldWrapper group={isCheckout}>
            <label>Email</label>
            <Select
              isSearchable={false}
              placeholder={t("Email")}
              options={formattedEmailOptions}
              value={currentlySelectedEmailOption}
              onChange={onSelectEmail}
              components={{
                IndicatorSeparator: null
              }}
              menuPlacement={direction}
              isDisabled={disabled}
              styles={{
                singleValue: (provided) => ({
                  ...provided,
                  ...styles.singleValue
                }),
                menuList: (provided) => ({
                  ...provided,
                  ...styles.menuList
                }),
                container: (provided) => ({
                  ...provided,
                  ...styles.container
                }),
                control: (provided) => ({
                  ...provided,
                  ...styles.control
                }),
                indicatorsContainer: (provided) => ({
                  ...provided,
                  ...styles.indicatorsContainer
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  ...styles.valueContainer
                })
              }}
            />
          </SingleFieldWrapper>
        )}
      </Fieldset>

      <SingleFieldWrapper group={isCheckout}>
        <label>{t("Address")}</label>
        <Select
          isSearchable={false}
          placeholder={t("Address")}
          options={formattedAddressOptions}
          value={currentlySelectedAddressOption}
          onChange={onSelectAddressData}
          components={{
            IndicatorSeparator: null
          }}
          menuPlacement={direction}
          isDisabled={disabled}
          styles={{
            singleValue: (provided) => ({
              ...provided,
              ...styles.singleValue
            }),
            menuList: (provided) => ({
              ...provided,
              ...styles.menuList
            }),
            container: (provided) => ({
              ...provided,
              ...styles.container
            }),
            control: (provided) => ({
              ...provided,
              ...styles.control
            }),
            indicatorsContainer: (provided) => ({
              ...provided,
              ...styles.indicatorsContainer
            }),
            valueContainer: (provided) => ({
              ...provided,
              ...styles.valueContainer
            })
          }}
        />
      </SingleFieldWrapper>

      {isCheckout && COUNTRIES_WITHOUT_TAXES.includes(countryCode) ? (
        <SingleFieldWrapper group={isCheckout}>
          <Input
            id="vat-number"
            label={t("VAT number")}
            placeholder="CHE123456789"
            onBlur={handleValidateVatNumber}
            error={!vatNumberValid}
            errorMessage={t("Please, ensure that the VAT number added is correct")}
          />
        </SingleFieldWrapper>
      ) : null}
    </MainWrapper>
  );
};

export default InvoiceAddressSelector;
